import {
  DEFAULT_LANGUAGE,
  LANGUAGE_STORAGE_KEY,
  Language,
  isSupportedLanguage,
} from './types';

/**
 * Language reported by the browser, which on every desktop OS follows the
 * machine's own language setting. `navigator.languages` is the ordered list
 * the person configured, so a machine set to English with Spanish second
 * resolves to English; a machine set to French with Spanish second still
 * lands on Spanish instead of falling back blindly.
 */
export const detectBrowserLanguage = (): Language => {
  if (typeof navigator === 'undefined') return DEFAULT_LANGUAGE;

  const candidates: string[] = [
    ...(navigator.languages ?? []),
    navigator.language,
  ].filter(Boolean) as string[];

  for (const candidate of candidates) {
    // 'es-CR' / 'en-GB' -> 'es' / 'en'
    const base = candidate.toLowerCase().split('-')[0];
    if (isSupportedLanguage(base)) return base;
  }

  return DEFAULT_LANGUAGE;
};

/** Last language used on this device, if any. */
export const readStoredLanguage = (): Language | null => {
  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return isSupportedLanguage(stored) ? stored : null;
  } catch {
    // Private mode / storage disabled: fall back to detection every load.
    return null;
  }
};

export const storeLanguage = (language: Language): void => {
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // Non-fatal: the account copy is the durable one.
  }
};

/**
 * Language to paint the very first render with, before the account row has
 * loaded: the device's last choice, otherwise the machine language.
 */
export const resolveInitialLanguage = (): Language =>
  readStoredLanguage() ?? detectBrowserLanguage();
